/**
 * Ejar bookings and the nightly fleet status record, both written by the engine
 * (admin SDK) and read here only.
 *
 *   ejar_bookings/<booking no>   one row per booking (every status), refreshed with the hourly
 *                                Ejar sync; `_metadata` holds { synced_at, rows }.
 *                                Fields: booking_number, broker_reference, vehicle, plate_no,
 *                                customer, mobile, pickup_date / dropoff_date ("dd/mm/yyyy HH:MM",
 *                                Dubai), pickup_day ("yyyy-mm-dd"), pickup_branch, dropoff_branch,
 *                                status, status_id (1000 open, 1001 became a contract,
 *                                1002 expired / no-show, 1003 cancelled), booking_type, duration,
 *                                source, payment_term, created_by, created_at, last_update.
 *   fleet_snapshots/<yyyy-mm-dd> taken at 23:59 Dubai: { total, rta_total, by_status,
 *                                rta_by_status, cars: [{ plate, status, branch, rta }] }.
 */
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { bookingsForDays, bookingsMetaOf, bookingsPageDays } from './storeData';
import { finesService } from './finesService';
import { isInvygoContract } from '../pages/ContractsManagement/utils/contractRows';

export const BOOKING_STATUS = { OPEN: 1000, CONTRACT: 1001, EXPIRED: 1002, CANCELLED: 1003 };

/**
 * The status as one of: 'open' | 'pending' | 'contract' | 'noshow' | 'cancelled' | 'other'.
 * 'pending' = "Awaiting Payment Provider Confirmation" (an online booking whose payment is
 * not confirmed yet - still a coming pick-up; seen live 25 Sep 2026, id not in the web enum).
 */
export const bookingState = (b) => {
    if (/await|pending/i.test(String(b?.status || ''))) return 'pending';
    const id = Number(b?.status_id);
    if (id === BOOKING_STATUS.OPEN) return 'open';
    if (id === BOOKING_STATUS.CONTRACT) return 'contract';
    if (id === BOOKING_STATUS.EXPIRED) return 'noshow';
    if (id === BOOKING_STATUS.CANCELLED) return 'cancelled';
    const s = String(b?.status || '').toLowerCase();
    if (s.includes('open')) return 'open';
    if (s.includes('close')) return 'contract';
    if (s.includes('expire')) return 'noshow';
    if (s.includes('cancel')) return 'cancelled';
    return 'other';
};

/**
 * Paid or not, from Ejar's "Balance Payment" (what the customer PAID online, before VAT - 6 Oct 2026) and the type:
 *   { state: 'paid', amount }                paid `amount` online (a bot booking: nothing left to pay, no amount)
 *   { state: 'due' }                         nothing paid online - pays at the branch (a bot booking: `amount` owed)
 *   { state: 'broker', amount }              a broker booking: the broker pays by invoice
 *   { state: 'awaiting' }                    online payment not confirmed yet
 *   { state: 'unknown' }                     no balance synced (older rows)
 *   { state: 'invygo' }                      an Invygo booking: paid to Invygo, never Yelo - shown '—', in no count
 * (the engine's booking_watch.payment_of is the same rule)
 */
export const bookingPayment = (b) => {
    // Invygo customers pay Invygo, never Yelo (owner, 28 Sep 2026): its zero balance is not 'Paid' and no amount on it
    // is Yelo's to collect - the Invygo branch decides, whatever the balance or the status says
    if (isInvygoContract(b)) return { state: 'invygo' };
    if (bookingState(b) === 'pending') return { state: 'awaiting' };
    const raw = b?.balance;
    if (raw === undefined || raw === null || String(raw).trim() === '') return { state: 'unknown' };
    const amount = Number(String(raw).replace(/,/g, ''));
    if (!Number.isFinite(amount)) return { state: 'unknown' };
    const broker = /broker/i.test(String(b?.booking_type || '')) || /^B/i.test(String(b?.booking_number || ''));
    if (b?.manual) {
        // a booking typed in the bot (not on Ejar): its balance is what is still to pay - 0 = paid
        if (amount <= 0) return { state: 'paid' };
        return broker ? { state: 'broker', amount } : { state: 'due', amount };
    }
    // Ejar's booking list: balancePayment is what the customer PAID online, before VAT - not what is left (owner,
    // 6 Oct 2026: I26000D02477526 showed "Paid" with 0 and had not paid; live data: 433.50 -> its contract shows
    // 455.18 paid = 433.50 + 5 %). The engine's twin: fines_pipeline/booking_watch.payment_of
    if (broker) return { state: 'broker', amount };
    if (amount > 0) return { state: 'paid', amount };
    return { state: 'due' };
};

/**
 * A test booking of the website maker (owner, 1 Oct 2026: "الحجات اللى بتيجى وتتكنسل من Qoad Automation متظهرش فى اى
 * ارقام خالص"): customer "Qoad Automation" / an @qoad.com e-mail - in no count and no list. The engine never takes new
 * ones in (fines_pipeline/ejar_sync.is_test_booking, the same rule); this hides the ones stored before.
 */
const TEST_BOOKING_NAMES = ['qoad automation'];
const TEST_BOOKING_DOMAINS = ['qoad.com'];
export const isTestBooking = (b) => {
    const names = ['customer', 'customer_name', 'customerName', 'customerDisplayName']
        .map((k) => String(b?.[k] || '')).join(' ').replace(/\s+/g, ' ').trim().toLowerCase();
    const email = String(b?.customer_email || b?.customerEmail || b?.raw?.customerEmail || '').trim().toLowerCase();
    return TEST_BOOKING_NAMES.some((n) => names.includes(n)) || TEST_BOOKING_DOMAINS.some((d) => email.endsWith(`@${d}`));
};

/** "dd/mm/yyyy HH:MM" (Dubai) -> Date, or null. */
export const bookingTime = (text) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/.exec(String(text || '').trim());
    return m ? new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0)) : null;
};

/** "Full Size Sedan - 408 - 2025" -> "Full Size Sedan" (the car class, as in the fleet). */
export const bookingClass = (b) => String(b?.vehicle || '').split(' - ')[0].trim() || 'Unknown';

/** "Full Size Sedan - 408 - 2025" -> "408 · 2025" (the model and year Ejar booked; '' when absent). */
export const bookingModel = (b) => String(b?.vehicle || '').split(' - ').slice(1).map((x) => x.trim()).filter(Boolean).join(' · ');

/** A pick-up as "HH:MM dd/mm/yyyy" in Dubai time: Ejar's "dd/mm/yyyy HH:MM" (already Dubai) turned round, an ISO
 *  moment read in Asia/Dubai; '' when there is none. */
export const pickupTimeText = (raw) => {
    const text = String(raw || '').trim();
    const dmy = /^(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2})/.exec(text);
    if (dmy) return `${dmy[2]} ${dmy[1]}`;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return text;
    const when = /^\d{4}-\d{2}-\d{2}/.test(text) ? new Date(text) : null;
    if (!when || Number.isNaN(when.getTime())) return '';
    const part = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(when).map((x) => [x.type, x.value]));
    return `${part.hour}:${part.minute} ${part.day}/${part.month}/${part.year}`;
};

/** The booking-confirmation message staff send the customer on WhatsApp - the owner's own Arabic template, word for
 *  word (it goes to customers, so Arabic on purpose; owner, 17 Oct 2026), with this booking's number and pick-up. */
export const bookingConfirmMessage = (b) => [
    'أسعد الله اوقاتكم بكل الخير .',
    'تحية طيبه وبعد ..',
    'شركة الوفاق لحلول النقل (يلو) .تتمنى لكم رحلة سعيده . يرجى التكرم بتأكيد حجزكم :',
    `رقم: ${b?.booking_number || b?.id || ''}`,
    `في تمام الساعه:   ${pickupTimeText(b?.pickup_date).replace(' ', '   ')}`,
    '',
    'يوجد وديعة بقيمة 1000 درهم. تأمين المخالفات المرورية المتأخرة. إذا كان لديك بطاقة ائتمانية، فسوف نقوم بحجز مبلغ 1000 درهم إماراتي لمدة 14 يومًا ثم نعيد المبلغ تلقائيًا وإذا كان نقدًا لمدة 30 يومًا من تاريخ الإرجاع..',
    '',
    'المبلغ المذكور في الطلب لا يشمل ضريبة حكومية بنسبة 5%، وتدفع الضريبة بنسبة 5% عند فتح العقد',
].join('\n');

export const bookingsService = {
    /** Bookings whose pick-up day is between fromIso and toIso (inclusive, "yyyy-mm-dd"); never a test booking. */
    async forDays(fromIso, toIso) {
        // the store (USE_CONTRACTS_V2): Ejar's bookings as the engine keeps them in PostgreSQL, same fields
        if (USE_CONTRACTS_V2) return ((await bookingsForDays(fromIso, toIso)) || []).filter((b) => !isTestBooking(b));
        const q = query(collection(db, 'ejar_bookings'), where('pickup_day', '>=', fromIso), where('pickup_day', '<=', toIso));
        const snap = await getDocs(q);
        return snap.docs.filter((d) => !d.id.startsWith('_')).map((d) => ({ id: d.id, ...d.data() }))
            .filter((b) => !isTestBooking(b));
    },

    /** Bookings made outside Ejar (WhatsApp / phone, written by the engine) with a pick-up in the days. */
    async manualForDays(fromIso, toIso) {
        try {
            // the store (USE_CONTRACTS_V2): GET /api/store/manual-bookings - the same documents ({id, ...booking, manual})
            if (USE_CONTRACTS_V2) return (await finesService.storeManualBookings({ from: fromIso, to: toIso }))?.items || [];
            const q = query(collection(db, 'manual_bookings'), where('pickup_day', '>=', fromIso), where('pickup_day', '<=', toIso));
            const snap = await getDocs(q);
            return snap.docs.map((d) => ({ id: d.id, ...d.data(), manual: true }));
        } catch (e) {
            logger.warn('[bookings] outside-Ejar bookings not readable:', e);
            return [];
        }
    },

    /** { synced_at, rows } of the last engine sync, or null when bookings were never synced. */
    async meta() {
        // the store has no _metadata: the newest read of the bookings the page shows says how fresh they are (the
        // same read as the page's own - one request)
        if (USE_CONTRACTS_V2) {
            try {
                const [from, to] = bookingsPageDays();
                return bookingsMetaOf(await bookingsForDays(from, to));
            } catch (e) {
                logger.warn('[bookings] store read failed:', e);
                return null;
            }
        }
        try {
            const snap = await getDoc(doc(db, 'ejar_bookings', '_metadata'));
            return snap.exists() ? snap.data() : null;
        } catch (e) {
            logger.warn('[bookings] metadata read failed:', e);
            return null;
        }
    },
};

export const fleetSnapshotService = {
    /** The fleet status record of a past day ("yyyy-mm-dd"), or null. */
    async get(dayIso) {
        // the store (USE_CONTRACTS_V2): GET /api/store/fleet-snapshots/{day} -> {snapshot: the document | null}
        if (USE_CONTRACTS_V2) return (await finesService.storeFleetSnapshot(dayIso))?.snapshot || null;
        const snap = await getDoc(doc(db, 'fleet_snapshots', dayIso));
        return snap.exists() ? snap.data() : null;
    },
};

export default bookingsService;
